// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  BookingStatus,
  Cart,
  CartStatus,
  EventFilter,
} from "../../../api/src/types";
import type {
  CartGetResponse,
  CartGetByIdResponse,
} from "../../../api/src/users/carts/route";
import type {
  CartItemPatchResponse,
  CartItemDeleteResponse,
  CartItemPostResponse,
} from "../../../api/src/users/carts/cart-items/route";
import type { CheckoutPostResponse } from "../../../api/src/users/checkout/route";
import type { PromoCodePostResponse } from "../../../api/src/users/carts/promo-codes/route";
import type {
  PurchaseItemPatchResponse,
  PurchaseItemsGetResponse,
  PurchaseItemWithEventsGetResponse,
} from "../../../api/src/users/purchase-items/route";
import type { ProductsGetResponse } from "../../../api/src/products/route";
import { PurchaseItemRefundResponse } from "../../../api/src/admin/purchase-items/route";
import { documentsApi } from "./documents";
import { usersApi } from "./users";
import { baseQuery } from "./utils";
import type { getClientAnalyticsContext } from "../utils/rudderstack";

type CheckoutAnalyticsContext = ReturnType<typeof getClientAnalyticsContext>;

export const paymentsApi = createApi({
  reducerPath: "paymentsApi",
  baseQuery,
  tagTypes: ["purchaseItems", "carts", "products"],
  endpoints: (builder) => ({
    getProducts: builder.query<ProductsGetResponse, void>({
      query: () => ({
        url: "products",
        method: "GET",
      }),
      providesTags: ["products"],
    }),
    getAttorneyServicesPurchasedByUser: builder.query<
      PurchaseItemsGetResponse,
      { id: string; documentId: string }
    >({
      query: ({ id, documentId }) => ({
        url: `users/${id}/purchase-items`,
        method: "GET",
        params: {
          category: "ATTORNEY_SERVICE",
          documentId,
        },
      }),
      providesTags: ["purchaseItems"],
    }),
    getPurchaseItemsByUser: builder.query<
      PurchaseItemsGetResponse,
      { id: string; documentId: string }
    >({
      query: ({ id, documentId }) => ({
        url: `users/${id}/purchase-items`,
        method: "GET",
        params: { documentId },
      }),
      providesTags: ["purchaseItems"],
    }),
    getCartsByUser: builder.query<
      CartGetResponse,
      { id: string; transactionStatus: string; documentId: string }
    >({
      query: ({ id, transactionStatus, documentId }) => ({
        url: `users/${id}/carts`,
        method: "GET",
        params: {
          transactionStatus,
          documentId,
        },
      }),
      providesTags: ["carts"],
    }),
    getCartById: builder.query<
      CartGetByIdResponse,
      { id: string; userId: string; documentId: string }
    >({
      query: ({ id, userId, documentId }) => ({
        url: `users/${userId}/carts/${id}`,
        method: "GET",
        params: { documentId },
      }),
      providesTags: ["carts"],
    }),
    createCart: builder.mutation<
      Cart,
      { id: string; documentId: string; cartStatus: CartStatus }
    >({
      query: ({ id, cartStatus, documentId }) => ({
        url: `users/${id}/carts`,
        method: "POST",
        params: { documentId },
        body: {
          cartStatus,
        },
      }),
      invalidatesTags: ["carts"],
    }),

    addCartItem: builder.mutation<
      CartItemPostResponse,
      {
        userId: string;
        cartId: string;
        documentId: string;
        productId: string;
        quantity: number;
        chosenAttorneyId?: string;
      }
    >({
      query: ({
        userId,
        cartId,
        documentId,
        productId,
        quantity,
        chosenAttorneyId,
      }) => ({
        url: `users/${userId}/carts/${cartId}/cart-items`,
        method: "POST",
        params: { documentId },
        body: {
          quantity,
          productId,
          ...(chosenAttorneyId && { chosenAttorneyId }),
        },
      }),
      invalidatesTags: ["carts"],
    }),
    updateCartItem: builder.mutation<
      CartItemPatchResponse,
      {
        userId: string;
        cartId: string;
        documentId: string;
        cartItemId: string;
        quantity: number;
        deletedAt?: Date;
        chosenAttorneyId?: string;
      }
    >({
      query: ({
        userId,
        cartId,
        documentId,
        cartItemId,
        quantity,
        deletedAt,
        chosenAttorneyId,
      }) => ({
        url: `users/${userId}/carts/${cartId}/cart-items/${cartItemId}`,
        method: "PATCH",
        params: { documentId },
        body: { quantity, deletedAt, chosenAttorneyId },
      }),
      invalidatesTags: ["carts"],
    }),
    removeCartItem: builder.mutation<
      CartItemDeleteResponse,
      { userId: string; cartId: string; documentId: string; cartItemId: string }
    >({
      query: ({ userId, cartId, documentId, cartItemId }) => ({
        url: `users/${userId}/carts/${cartId}/cart-items/${cartItemId}`,
        method: "DELETE",
        params: { documentId },
      }),
      invalidatesTags: ["carts"],
    }),
    createCheckout: builder.mutation<
      CheckoutPostResponse,
      {
        id: string;
        documentId: string;
        cartId: string;
        paymentMethod: "card" | "installments";
        analyticsContext?: CheckoutAnalyticsContext;
      }
    >({
      query: ({ id, documentId, cartId, paymentMethod, analyticsContext }) => ({
        url: `users/${id}/checkout`,
        method: "POST",
        params: { documentId },
        body: {
          cartId,
          paymentMethod,
          analyticsContext,
        },
      }),
    }),
    applyPromoCode: builder.mutation<
      PromoCodePostResponse,
      { userId: string; cartId: string; documentId: string; promoCode: string }
    >({
      query: ({ userId, cartId, documentId, promoCode }) => ({
        url: `users/${userId}/carts/${cartId}/promo-codes`,
        method: "POST",
        params: { documentId },
        body: {
          promoCode,
        },
      }),
      invalidatesTags: ["carts"],
    }),
    removePromoCode: builder.mutation<
      PromoCodePostResponse,
      { userId: string; cartId: string; documentId: string }
    >({
      query: ({ userId, cartId, documentId }) => ({
        url: `users/${userId}/carts/${cartId}/promo-codes`,
        method: "DELETE",
        params: { documentId },
      }),
      invalidatesTags: ["carts"],
    }),
    updatePurchaseItem: builder.mutation<
      PurchaseItemPatchResponse,
      {
        userId: string;
        purchaseItemId: string;
        bookingStatus: BookingStatus;
        attorneyId: string;
        documentId: string;
      }
    >({
      query: ({
        userId,
        purchaseItemId,
        bookingStatus,
        attorneyId,
        documentId,
      }) => ({
        url: `users/${userId}/purchase-items/${purchaseItemId}`,
        method: "PATCH",
        body: {
          bookingStatus,
          attorneyId,
          documentId,
        },
      }),
      invalidatesTags: ["purchaseItems"],
    }),
    getPurchaseItemsWithEvents: builder.query<
      PurchaseItemWithEventsGetResponse,
      { id: string; filter: EventFilter; documentId: string }
    >({
      query: ({ id, filter, documentId }) => ({
        url: `users/${id}/purchase-items/events`,
        method: "GET",
        params: { filter, documentId },
      }),
      providesTags: ["purchaseItems"],
    }),
    refundPurchaseItem: builder.mutation<
      PurchaseItemRefundResponse,
      {
        purchaseItemId: string;
        refundAmount: number | null;
        refundReason: string;
        isComplimentaryRefund?: boolean;
      }
    >({
      query: ({
        purchaseItemId,
        refundAmount,
        refundReason,
        isComplimentaryRefund,
      }) => ({
        url: `admin/purchase-items/${purchaseItemId}/refund`,
        method: "POST",
        body: {
          refundAmount,
          refundReason,
          isComplimentaryRefund: isComplimentaryRefund ?? false,
        },
      }),
      async onQueryStarted(_args, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(documentsApi.util.invalidateTags(["documents"]));
          dispatch(usersApi.util.invalidateTags(["AdminUsers"]));
        } catch (e) {
          console.error(e);
        }
      },
      invalidatesTags: ["purchaseItems"],
    }),
  }),
});

export const {
  useGetProductsQuery,
  useGetAttorneyServicesPurchasedByUserQuery,
  useGetPurchaseItemsByUserQuery,
  useGetCartsByUserQuery,
  useGetCartByIdQuery,
  useCreateCartMutation,
  useUpdateCartItemMutation,
  useRemoveCartItemMutation,
  useAddCartItemMutation,
  useCreateCheckoutMutation,
  useApplyPromoCodeMutation,
  useRemovePromoCodeMutation,
  useUpdatePurchaseItemMutation,
  useGetPurchaseItemsWithEventsQuery,
  useRefundPurchaseItemMutation,
} = paymentsApi;
